import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import type {
  AdminUser,
  CreateUserInput,
  CreateUserResult,
  Tool,
  UserStatus,
} from '../../api/types';
import {
  absoluteDate,
  emailKindLabel,
  initials,
  relativeTime,
  statusLabel,
} from '../../shared/format';
import {
  IconBuilding,
  IconCheckCircle,
  IconClose,
  IconInfo,
  IconMail,
  IconPlus,
  IconSearch,
} from '../../shared/icons';

const USER_COLUMNS = '2fr 1.4fr 1.1fr .9fr .9fr';

/** Which pill a status wears. Archived and declined are neutral rather than
 *  red: neither is an emergency, and a wall of red would make the list read
 *  as trouble when it is mostly routine. */
function statusBadgeClass(status: UserStatus): string {
  switch (status) {
    case 'active':
      return 'badge-ok';
    case 'pending_approval':
      return 'badge-brand';
    case 'invited':
      return 'badge-warn';
    case 'suspended':
      return 'badge-fail';
    case 'rejected':
    case 'archived':
      return 'badge-neutral';
  }
}

const EMPTY_USERS: AdminUser[] = [];
const EMPTY_TOOLS: Tool[] = [];

interface AdminPageProps {
  /** The signed-in administrator, so the UI can protect their own account. */
  currentUserId: string;
}

export function AdminPage({ currentUserId }: AdminPageProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', showArchived],
    queryFn: () => api.adminUsers({ includeArchived: showArchived }),
  });

  const users = usersQuery.data?.users ?? EMPTY_USERS;
  const catalogue = usersQuery.data?.tools ?? EMPTY_TOOLS;
  const counts = usersQuery.data?.counts ?? {};

  // Filtering happens here rather than on the server: the whole list arrives
  // in one request and is small enough that a round trip per keystroke would
  // be the slower of the two.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.display_name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle) ||
        u.company.toLowerCase().includes(needle),
    );
  }, [users, search]);

  const selected = users.find((u) => u.id === selectedId) ?? null;
  const waiting = users.filter((u) => u.status === 'pending_approval');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  return (
    <div style={{ display: 'flex', gap: 10, flex: 1, minHeight: 0 }}>
      <div className="panel panel-grow" style={{ flex: 1, minWidth: 0 }}>
        <div className="panel-hdr">
          <span>Users</span>
          <div className="panel-hdr-actions">
            {(counts.pending_approval ?? 0) > 0 && (
              <span className="badge badge-brand">{counts.pending_approval} awaiting review</span>
            )}
            <span className="badge badge-ok">{counts.active ?? 0} active</span>
            {(counts.invited ?? 0) > 0 && (
              <span className="badge badge-warn">{counts.invited} invited</span>
            )}
          </div>
        </div>

        {notice && (
          <div style={{ padding: '10px 12px 0' }}>
            <div className="notice notice-ok" role="status">
              <IconCheckCircle size={14} />
              <span style={{ flex: 1 }}>{notice}</span>
              <button className="link-button" onClick={() => setNotice(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* The queue is the job on this screen, so it says so in words rather
            than leaving the administrator to spot the rows. */}
        {waiting.length > 0 && !notice && (
          <div style={{ padding: '10px 12px 0' }}>
            <div className="notice" role="status">
              <IconInfo size={14} />
              <span>
                {waiting.length === 1
                  ? '1 sign-up is waiting for a decision.'
                  : `${waiting.length} sign-ups are waiting for a decision.`}{' '}
                They opened an account with a personal email address, so nothing is granted until
                you approve them.
              </span>
            </div>
          </div>
        )}

        <div className="toolbar">
          <div className="search">
            <IconSearch size={13} />
            <input
              type="search"
              placeholder="Search by name, email or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search users"
            />
          </div>
          <label className="checkbox-row" style={{ whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setSelectedId(null);
              }}
            />
            <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)' }}>
              Show archived
            </span>
          </label>
          <button className="btn btn-primary" onClick={() => setInviteOpen(true)}>
            <IconPlus size={12} />
            Invite someone
          </button>
        </div>

        <div className="data-head data-row" style={{ gridTemplateColumns: USER_COLUMNS }}>
          <span>User</span>
          <span>Company</span>
          <span>Tool access</span>
          <span>Last seen</span>
          <span className="cell-end">Status</span>
        </div>

        <div className="data-table">
          {usersQuery.isPending && <div className="empty-row">Loading…</div>}
          {usersQuery.isError && <div className="empty-row">Users could not be loaded.</div>}
          {usersQuery.isSuccess && filtered.length === 0 && (
            <div className="empty-row">
              {search ? `No users match “${search}”.` : 'Nobody has signed up yet.'}
            </div>
          )}

          {filtered.map((user) => (
            <button
              key={user.id}
              type="button"
              className={`data-row is-clickable${user.id === selectedId ? ' is-selected' : ''}`}
              style={{
                gridTemplateColumns: USER_COLUMNS,
                textAlign: 'left',
                border: 'none',
                background: 'none',
                font: 'inherit',
              }}
              onClick={() => setSelectedId(user.id)}
            >
              <span className="user-cell">
                <span
                  className={`user-avatar${user.is_platform_admin ? ' is-admin' : ''}${
                    user.status === 'invited' || user.status === 'archived' ? ' is-pending' : ''
                  }`}
                >
                  {user.status === 'invited' ? '?' : initials(user.display_name, user.email)}
                </span>
                <span className="user-cell-text">
                  <span className="cell-primary">
                    {user.display_name}
                    {user.is_platform_admin && (
                      <span className="chip" style={{ marginLeft: 6 }}>
                        EJOT
                      </span>
                    )}
                  </span>
                  <span className="cell-muted">{user.email}</span>
                </span>
              </span>

              <span className="user-cell-text">
                <span className="cell-primary">{user.company || '—'}</span>
                <span className="cell-muted">
                  {[user.city, user.country].filter(Boolean).join(', ') || '—'}
                </span>
              </span>

              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {user.tool_keys.length === 0 ? (
                  <span className="chip chip-empty">None</span>
                ) : (
                  user.tool_keys.map((key) => (
                    <span className="chip chip-ok" key={key}>
                      {catalogue.find((t) => t.key === key)?.name ?? key}
                    </span>
                  ))
                )}
              </span>

              <span className="cell-muted">{relativeTime(user.last_seen_at)}</span>

              <span className={`badge cell-end ${statusBadgeClass(user.status)}`}>
                {statusLabel(user.status)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <UserDrawer
          user={selected}
          catalogue={catalogue}
          isSelf={selected.id === currentUserId}
          onClose={() => setSelectedId(null)}
          onChanged={invalidate}
          onNotice={setNotice}
        />
      )}

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onCreated={(result) => {
            setInviteOpen(false);
            setSelectedId(result.user.id);
            // A created account with no invitation out is half-finished; the
            // drawer's "Send invitation again" is where that gets fixed.
            setNotice(
              result.invitation_sent
                ? `Invitation sent to ${result.user.email}.`
                : `${result.user.email} was created, but the invitation could not be sent.`,
            );
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────────────

interface UserDrawerProps {
  user: AdminUser;
  catalogue: Tool[];
  isSelf: boolean;
  onClose: () => void;
  onChanged: () => void;
  onNotice: (message: string) => void;
}

function UserDrawer({ user, catalogue, isSelf, onClose, onChanged, onNotice }: UserDrawerProps) {
  const archived = user.status === 'archived';
  const pending = user.status === 'pending_approval';
  const rejected = user.status === 'rejected';

  const update = useMutation({
    mutationFn: (input: { status?: UserStatus }) => api.updateUser(user.id, input),
    onSuccess: onChanged,
  });

  const approve = useMutation({
    mutationFn: () => api.approveUser(user.id),
    onSuccess: (updated) => {
      onNotice(`${updated.email} is approved and has every tool.`);
      onChanged();
    },
  });

  const reject = useMutation({
    mutationFn: () => api.rejectUser(user.id),
    onSuccess: (updated) => {
      onNotice(`${updated.email} was declined and told so.`);
      onChanged();
    },
  });

  const setGrants = useMutation({
    mutationFn: (toolKeys: string[]) => api.setGrants(user.id, toolKeys),
    onSuccess: onChanged,
  });

  const sendInvitation = useMutation({
    mutationFn: () => api.sendInvitation(user.id),
    onSuccess: onChanged,
  });

  const revokeSessions = useMutation({
    mutationFn: () => api.revokeSessions(user.id),
    onSuccess: onChanged,
  });

  const error = [
    update.error,
    approve.error,
    reject.error,
    setGrants.error,
    sendInvitation.error,
    revokeSessions.error,
  ].find((e): e is ApiError => e instanceof ApiError);

  const toggleGrant = (key: string) => {
    const next = user.tool_keys.includes(key)
      ? user.tool_keys.filter((k) => k !== key)
      : [...user.tool_keys, key];
    setGrants.mutate(next);
  };

  return (
    <div className="panel" style={{ width: 340, flexShrink: 0, overflowY: 'auto' }}>
      <div className="panel-hdr">
        <span>{pending ? 'Waiting for a decision' : 'Selected user'}</span>
        <button
          className="btn"
          style={{ padding: '2px 6px', border: 'none', background: 'none' }}
          onClick={onClose}
          aria-label="Close"
        >
          <IconClose size={13} />
        </button>
      </div>

      <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span
            className={`user-avatar${user.is_platform_admin ? ' is-admin' : ''}`}
            style={{ width: 42, height: 42, borderRadius: 6, fontSize: 15 }}
          >
            {initials(user.display_name, user.email)}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{user.display_name}</span>
            <span className="cell-muted">{user.email}</span>
          </div>
        </div>

        {/* What the sign-up said about itself. This is the whole basis for a
            decision, so it sits at the top for a pending account. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="divider">
            <span>What they told us</span>
          </div>
          <DetailRow label="Company" value={user.company || '—'} />
          <DetailRow
            label="Location"
            value={[user.city, user.country].filter(Boolean).join(', ') || '—'}
          />
          <DetailRow label="Phone" value={user.phone || '—'} />
          <DetailRow label="Address" value={emailKindLabel(user.email_kind)} />
        </div>

        {pending && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div className="notice" role="status">
              <IconBuilding size={14} />
              <span>
                They confirmed this address, but it is a personal mailbox, so nothing has been
                granted. Approving opens every released tool to them.
              </span>
            </div>
            <button
              className="btn btn-primary btn-block"
              disabled={approve.isPending || reject.isPending}
              onClick={() => approve.mutate()}
            >
              {approve.isPending ? 'Approving…' : 'Approve and grant every tool'}
            </button>
            <button
              className="btn btn-danger btn-block"
              disabled={approve.isPending || reject.isPending}
              onClick={() => reject.mutate()}
            >
              {reject.isPending ? 'Declining…' : 'Decline'}
            </button>
            <span className="field-help">
              Declining keeps the record, so the address stays taken and the same person cannot
              simply sign up again. They are told, without a reason, and you can approve them
              later if this turns out to be wrong.
            </span>
          </div>
        )}

        {rejected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <button
              className="btn btn-quiet-primary btn-block"
              disabled={approve.isPending}
              onClick={() => approve.mutate()}
            >
              Approve after all
            </button>
            <span className="field-help">
              Declined on {absoluteDate(user.rejected_at)}. Approving now opens the account with
              the password they already chose.
            </span>
          </div>
        )}

        {!pending && !rejected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div className="divider">
              <span>Tool access</span>
            </div>
            <span className="field-help">
              Every account gets every released tool. Turning one off here is the exception, for
              when one person should not have one of them.
            </span>

            {catalogue.map((tool) => {
              const granted = user.tool_keys.includes(tool.key);
              return (
                <div
                  key={tool.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '9px 10px',
                    border: '1px solid var(--border-light)',
                    borderRadius: 6,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span className="cell-primary">{tool.name}</span>
                    <span className="field-help">
                      {tool.status === 'available' ? 'Released' : 'Not released yet'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`toggle${granted ? ' is-on' : ''}`}
                    role="switch"
                    aria-checked={granted}
                    aria-label={`${granted ? 'Revoke' : 'Grant'} ${tool.name}`}
                    disabled={archived || setGrants.isPending}
                    onClick={() => toggleGrant(tool.key)}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="divider">
            <span>Account</span>
          </div>
          <DetailRow label="Status" value={statusLabel(user.status)} />
          <DetailRow label="Signed up" value={absoluteDate(user.created_at)} />
          <DetailRow label="Last sign-in" value={relativeTime(user.last_seen_at)} />
          {user.approved_at && <DetailRow label="Approved" value={absoluteDate(user.approved_at)} />}
          {archived && <DetailRow label="Archived" value={absoluteDate(user.archived_at)} />}
          {user.is_platform_admin && <DetailRow label="Platform" value="EJOT operator" />}
        </div>

        {sendInvitation.isSuccess && (
          <div className="notice notice-ok" role="status">
            <IconCheckCircle size={14} />
            <span>
              {user.status === 'active'
                ? 'A password-reset link is on its way.'
                : 'The invitation has been sent again.'}
            </span>
          </div>
        )}

        {archived ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <button
              className="btn btn-quiet-primary btn-block"
              disabled={update.isPending}
              onClick={() => update.mutate({ status: 'active' })}
            >
              Restore account
            </button>
            <span className="field-help">
              Restoring brings the account back as invited. The password was cleared when it was
              archived, so the person chooses a new one through a fresh invitation.
            </span>
          </div>
        ) : (
          !pending &&
          !rejected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <button
                className="btn btn-quiet-primary btn-block"
                disabled={sendInvitation.isPending || user.status === 'suspended'}
                onClick={() => sendInvitation.mutate()}
              >
                <IconMail size={12} />
                {user.status === 'active' ? 'Send password-reset link' : 'Send invitation again'}
              </button>
              <span className="field-help">
                {user.status === 'active'
                  ? 'They choose the new password themselves; nobody here can set it for them.'
                  : 'The account cannot sign in until the invitation is accepted.'}
              </span>

              <div style={{ display: 'flex', gap: 7, marginTop: 4 }}>
                <button
                  className="btn"
                  style={{ flex: 1 }}
                  disabled={revokeSessions.isPending}
                  onClick={() => revokeSessions.mutate()}
                >
                  Sign out everywhere
                </button>
                {!isSelf && (
                  <button
                    className="btn"
                    style={{ flex: 1 }}
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate({ status: user.status === 'suspended' ? 'active' : 'suspended' })
                    }
                  >
                    {user.status === 'suspended' ? 'Reinstate' : 'Suspend'}
                  </button>
                )}
              </div>

              {!isSelf && (
                <>
                  <button
                    className="btn btn-danger btn-block"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ status: 'archived' })}
                  >
                    Archive account
                  </button>
                  <span className="field-help">
                    Archiving is what replaces deleting. The record stays, so the address is not
                    handed to somebody else, but the password and every tool grant go.
                  </span>
                </>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
      <span className="cell-secondary">{label}</span>
      <span className="cell-primary" style={{ textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

// ── Invite dialog ───────────────────────────────────────────────────────────

interface InviteDialogProps {
  onClose: () => void;
  onCreated: (result: CreateUserResult) => void;
}

/**
 * Inviting somebody directly.
 *
 * The invitation is itself the vouching, so the account never joins the
 * approval queue, whatever address it uses. There is no tool picker here: the
 * platform's rule is that everybody gets everything, and taking one away is a
 * thing you do to an existing account, not a decision you make up front.
 */
function InviteDialog({ onClose, onCreated }: InviteDialogProps) {
  const [form, setForm] = useState<CreateUserInput>({
    email: '',
    display_name: '',
    company: '',
  });

  // A generated password is shown here once, before the dialog closes, so
// the administrator can pass it on. It is never shown again.
const [issued, setIssued] = useState<CreateUserResult | null>(null);
const [copied, setCopied] = useState(false);

const create = useMutation({
mutationFn: () => api.createUser(form),
onSuccess: (result) => {
if (result.temporary_password) {
setIssued(result);
} else {
onCreated(result);
}
},
});

  const error = create.error instanceof ApiError ? create.error : null;

if (issued?.temporary_password) {
const password = issued.temporary_password;
return (
<div className="panel" style={{ width: 340, flexShrink: 0, overflowY: 'auto' }}>
<div className="panel-hdr">
<span>Temporary password</span>
</div>
<div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
<div className="notice notice-error" role="alert">
<IconInfo size={14} />
<span>
Shown once. Pass it to {issued.user.email} now. It cannot be retrieved later.
</span>
</div>
<code
style={{
fontSize: 15,
fontWeight: 700,
padding: '10px 12px',
border: '1px solid var(--border-light)',
borderRadius: 6,
background: 'var(--surface-app)',
wordBreak: 'break-all',
userSelect: 'all',
}}
>
{password}
</code>
<button
className="btn btn-block"
type="button"
onClick={async () => {
try {
await navigator.clipboard.writeText(password);
setCopied(true);
} catch {
setCopied(false);
}
}}
>
{copied ? 'Copied' : 'Copy password'}
</button>
<span className="field-help">
They will be asked to choose their own password the first time they sign in.
</span>
<button
className="btn btn-primary btn-block"
type="button"
onClick={() => onCreated(issued)}
>
Done
</button>
</div>
</div>
);
}

return (
<div className="panel" style={{ width: 340, flexShrink: 0, overflowY: 'auto' }}>
      <div className="panel-hdr">
        <span>Invite someone</span>
        <button
          className="btn"
          style={{ padding: '2px 6px', border: 'none', background: 'none' }}
          onClick={onClose}
          aria-label="Close"
        >
          <IconClose size={13} />
        </button>
      </div>

      <form
        className="panel-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}

        <div className="field">
          <label className="lbl" htmlFor="invite-name">
            <span className="lbl-text">Name</span>
          </label>
          <input
            id="invite-name"
            className="inp inp-compact"
            required
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="invite-email">
            <span className="lbl-text">Email</span>
          </label>
          <input
            id="invite-email"
            className="inp inp-compact"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <span className="field-help">
            Inviting somebody is the same as approving them, so a personal address is fine here.
          </span>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="invite-company">
            <span className="lbl-text">Company</span>
          </label>
          <input
            id="invite-company"
            className="inp inp-compact"
            value={form.company ?? ''}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </div>

        <label className="checkbox-row">
<input
type="checkbox"
checked={!!form.temporary_password}
onChange={(e) => setForm({ ...form, temporary_password: e.target.checked })}
/>
<span className="lbl-text">Set a temporary password instead of sending an invitation</span>
</label>

<div className="notice">
<IconMail size={14} />
<span>
{form.temporary_password
? 'The platform generates the password and shows it to you once. The person must replace it the first time they sign in. Every released tool is granted.'
: 'An invitation goes to this address and every released tool is granted. They choose their own password — nobody here ever sets it for them.'}
</span>
</div>

        <button className="btn btn-primary btn-block" type="submit" disabled={create.isPending}>
          {create.isPending
? 'Creating…'
: form.temporary_password
? 'Create account'
: 'Create account and send invitation'}
        </button>
      </form>
    </div>
  );
}
